import Decimal from "decimal.js-light";

import { MM_PER_METRE, SQM_METRE_STORAGE_DECIMALS, SQM_MM_ENTRY_DECIMAL_PLACES } from "./constant";

/**
 * The two units a user may type square-metre dimensions in.
 * Stored length and width are ALWAYS canonical metres — the unit only records
 * how the number was typed, so we can show it back the same way.
 *
 * This is metric-only. Square-feet rows never carry this field and must not be
 * passed through these helpers; they keep using the sq ft conversion utilities.
 */
export const SQUARE_METRE_ENTRY_UNITS = {
  METRES: "m",
  MILLIMETRES: "mm",
};

const VALID_UNITS = [SQUARE_METRE_ENTRY_UNITS.METRES, SQUARE_METRE_ENTRY_UNITS.MILLIMETRES];

/**
 * Works out which unit a stored row was entered in.
 * Rows saved before this feature have no unit (or a value we don't recognise),
 * and those were always typed in metres — so anything unexpected means metres.
 *
 * @param {string|null|undefined} unit - The stored provenance value
 * @returns {"m"|"mm"} The effective entry unit
 */
export const normalizeSquareMetreEntryUnit = (unit) =>
  VALID_UNITS.includes(unit) ? unit : SQUARE_METRE_ENTRY_UNITS.METRES;

/**
 * Turns what the user typed into the canonical metres we store.
 * Millimetres are divided by 1000 exactly, so 2500.123 mm becomes 2.500123 m.
 * Metres pass straight through, unchanged from the existing behaviour.
 *
 * @param {Object} [params]
 * @param {number|string} [params.value=0] - The typed length or width
 * @param {string} [params.unit] - The unit it was typed in
 * @returns {number} The value in metres
 */
export const convertSquareMetreDimensionToMetres = ({ value = 0, unit } = {}) => {
  const amount = Decimal(Number(value) || 0);

  if (normalizeSquareMetreEntryUnit(unit) !== SQUARE_METRE_ENTRY_UNITS.MILLIMETRES) {
    return parseFloat(amount);
  }

  return parseFloat(amount.dividedBy(MM_PER_METRE).toDecimalPlaces(SQM_METRE_STORAGE_DECIMALS));
};

/**
 * Turns canonical metres back into the number the user originally typed,
 * so editing and display show 1200 rather than 1.2 for a millimetre row.
 * Rounding to 3 decimals then dropping trailing zeros keeps 1.2 m as "1200",
 * not "1200.0000000001".
 *
 * @param {Object} [params]
 * @param {number|string} [params.metres=0] - The stored canonical value in metres
 * @param {string} [params.unit] - The unit the row was entered in
 * @returns {number} The value expressed in the entry unit
 */
export const convertMetresToSquareMetreDimension = ({ metres = 0, unit } = {}) => {
  const amount = Decimal(Number(metres) || 0);

  if (normalizeSquareMetreEntryUnit(unit) !== SQUARE_METRE_ENTRY_UNITS.MILLIMETRES) {
    return parseFloat(amount);
  }

  return parseFloat(amount.times(MM_PER_METRE).toDecimalPlaces(SQM_MM_ENTRY_DECIMAL_PLACES));
};

/**
 * Checks a typed length or width before we accept it.
 * Metres keep the existing rule (must be a number). Millimetres add one more:
 * at most three decimal places, because that is all we can store exactly.
 *
 * @param {number|string} value - The typed value
 * @param {string} [unit] - The unit it was typed in
 * @returns {boolean} True when the value can be accepted
 */
const isValidSquareMetreDimensionFormat = (value, unit) => {
  if (value === undefined || value === null) {
    return false;
  }

  const trimmed = String(value).trim();
  if (!/^\d+(\.\d*)?$|^\.\d+$/.test(trimmed)) {
    return false;
  }

  if (normalizeSquareMetreEntryUnit(unit) !== SQUARE_METRE_ENTRY_UNITS.MILLIMETRES) {
    return true;
  }

  const [, decimals = ""] = trimmed.split(".");
  return decimals.length <= SQM_MM_ENTRY_DECIMAL_PLACES;
};

/**
 * Reads the company setting that decides which unit new drafts start in.
 * A company that has not set it (or cannot have it) falls back to metres.
 *
 * @param {boolean|null|undefined} isMillimetreEntryEnabled - The company setting
 * @returns {"m"|"mm"} The unit a brand new draft should use
 */
export const getSquareMetreUnitFromSetting = (isMillimetreEntryEnabled) =>
  isMillimetreEntryEnabled === true
    ? SQUARE_METRE_ENTRY_UNITS.MILLIMETRES
    : SQUARE_METRE_ENTRY_UNITS.METRES;

/**
 * Checks a typed dimension before we let the row be added.
 * Adds the "not zero" rule on top of the format rules, because a zero-sided
 * piece has no area and used to slip through as the string "0".
 *
 * @param {Object} [params]
 * @param {number|string} params.value - The typed length or width
 * @param {string} [params.unit] - The unit it was typed in
 * @returns {boolean} True when the dimension can be added
 */
export const isAcceptableSquareMetreDimension = ({ value, unit } = {}) =>
  isValidSquareMetreDimensionFormat(value, unit) && parseFloat(value) > 0;
